import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { FileText, Plus, MoreHorizontal, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "./shared";
import { printDocument } from "./accountingPrint";

const STATUS_COLOR = {
    draft: "bg-bone2 text-espresso",
    sent: "bg-gold/15 text-goldDark",
    paid: "bg-forest/10 text-forest",
};

const emptyDoc = (type) => ({
    type, guest_name: "", guest_email: "",
    line_items: [{ description: "", quantity: 1, unit_price: 0 }],
    discount: 0, status: "draft", issue_date: new Date().toISOString().slice(0, 10), due_date: "", notes: "",
});

export default function AccountingTab() {
    const [docs, setDocs] = useState([]);
    const [filter, setFilter] = useState("all");
    const [editing, setEditing] = useState(null);
    const [bookingPicker, setBookingPicker] = useState(false);

    const refresh = () => api.get("/admin/accounting/documents").then((r) => setDocs(r.data));
    useEffect(() => { refresh(); }, []);

    const filtered = filter === "all" ? docs : docs.filter((d) => d.type === filter);

    const send = async (id) => {
        try { const r = await api.post(`/admin/accounting/documents/${id}/send`); 
            toast.success(r.data.email?.status === "skipped" ? "No email provider configured — set one in Settings." : "Sent to guest"); refresh();
        } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    };
    const convert = async (id) => {
        try { await api.post(`/admin/accounting/documents/${id}/convert`); toast.success("Converted to invoice"); refresh(); }
        catch (e) { toast.error(e.response?.data?.detail || "Convert failed"); }
    };
    const remove = async (id) => {
        if (!window.confirm("Delete this document?")) return;
        await api.delete(`/admin/accounting/documents/${id}`); toast.success("Deleted"); refresh();
    };

    return (
        <div data-testid="admin-accounting">
            <PageHeader
                eyebrow="Admin · Accounting"
                title="Invoices & quotes."
                subtitle="Generate invoices and quotes, send them to guests, and print or download as PDF."
                action={
                    <div className="flex gap-2 flex-wrap">
                        <button onClick={() => setBookingPicker(true)} data-testid="acct-from-booking" className="inline-flex items-center gap-2 px-4 py-3 border border-espresso/30 text-xs uppercase tracking-widest2 font-semibold hover:bg-bone2"><FileText size={14} /> From booking</button>
                        <button onClick={() => setEditing(emptyDoc("quote"))} data-testid="acct-new-quote" className="inline-flex items-center gap-2 px-4 py-3 border border-espresso/30 text-xs uppercase tracking-widest2 font-semibold hover:bg-bone2"><Plus size={14} /> New quote</button>
                        <button onClick={() => setEditing(emptyDoc("invoice"))} data-testid="acct-new-invoice" className="inline-flex items-center gap-2 px-4 py-3 bg-forest text-bone text-xs uppercase tracking-widest2 font-semibold"><Plus size={14} /> New invoice</button>
                    </div>
                }
            />
            <div className="flex gap-2 mb-6 flex-wrap">
                {["all", "invoice", "quote"].map((s) => (
                    <button key={s} onClick={() => setFilter(s)} data-testid={`acct-filter-${s}`} className={`px-4 py-2 text-xs uppercase tracking-widest2 border ${filter === s ? "bg-forest text-bone border-forest" : "border-espresso/20 text-espresso hover:bg-bone2"}`}>{s}</button>
                ))}
            </div>

            <div className="border border-espresso/15 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="text-left text-xs uppercase tracking-widest2 text-espresso bg-bone2/50">
                        <tr>
                            <th className="px-4 py-3">Number</th>
                            <th className="px-4 py-3">Type</th>
                            <th className="px-4 py-3">Guest</th>
                            <th className="px-4 py-3">Total</th>
                            <th className="px-4 py-3">Status</th>
                            <th className="px-4 py-3">Issued</th>
                            <th className="px-4 py-3" />
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-muted2">No documents yet.</td></tr>}
                        {filtered.map((d) => (
                            <tr key={d.id} className="border-t border-espresso/10" data-testid={`acct-row-${d.id}`}>
                                <td className="px-4 py-4 font-medium font-mono">{d.number}</td>
                                <td className="px-4 py-4 capitalize">{d.type}</td>
                                <td className="px-4 py-4">{d.guest_name || "—"}<div className="text-xs text-muted2">{d.guest_email}</div></td>
                                <td className="px-4 py-4 font-serif text-lg">${d.total.toFixed(2)}</td>
                                <td className="px-4 py-4"><span className={`px-2 py-1 text-[10px] uppercase tracking-widest2 ${STATUS_COLOR[d.status]}`}>{d.status}</span></td>
                                <td className="px-4 py-4 text-xs text-muted2">{d.issue_date}</td>
                                <td className="px-4 py-4 text-right">
                                    <DropdownMenu>
                                        <DropdownMenuTrigger data-testid={`acct-actions-${d.id}`} className="p-2 hover:bg-bone2 rounded-sm"><MoreHorizontal size={16} /></DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                            <DropdownMenuItem data-testid={`acct-edit-${d.id}`} onClick={() => setEditing(d)}>Edit</DropdownMenuItem>
                                            <DropdownMenuItem data-testid={`acct-print-${d.id}`} onClick={() => printDocument(d)}>Print / PDF</DropdownMenuItem>
                                            <DropdownMenuItem data-testid={`acct-send-${d.id}`} onClick={() => send(d.id)}>Send to guest</DropdownMenuItem>
                                            {d.type === "quote" && <DropdownMenuItem data-testid={`acct-convert-${d.id}`} onClick={() => convert(d.id)}>Convert to invoice</DropdownMenuItem>}
                                            <DropdownMenuItem data-testid={`acct-delete-${d.id}`} onClick={() => remove(d.id)} className="text-terracotta">Delete</DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {editing && <DocEditor doc={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />}
            {bookingPicker && <BookingPicker onClose={() => setBookingPicker(false)} onCreated={() => { setBookingPicker(false); refresh(); }} />}
        </div>
    );
}

function DocEditor({ doc, onClose, onSaved }) {
    const rid = () => Math.random().toString(36).slice(2);
    const [form, setForm] = useState({
        guest_name: doc.guest_name || "", guest_email: doc.guest_email || "",
        line_items: (doc.line_items?.length ? doc.line_items : [{ description: "", quantity: 1, unit_price: 0 }]).map((l) => ({ ...l, _k: rid() })),
        discount: doc.discount || 0, status: doc.status || "draft",
        issue_date: doc.issue_date || new Date().toISOString().slice(0, 10),
        due_date: doc.due_date || "", notes: doc.notes || "", currency: doc.currency || "USD",
    });
    const [saving, setSaving] = useState(false);

    const setLine = (i, k, v) => setForm((f) => ({ ...f, line_items: f.line_items.map((l, idx) => idx === i ? { ...l, [k]: v } : l) }));
    const addLine = () => setForm((f) => ({ ...f, line_items: [...f.line_items, { description: "", quantity: 1, unit_price: 0, _k: rid() }] }));
    const removeLine = (i) => setForm((f) => ({ ...f, line_items: f.line_items.filter((_, idx) => idx !== i) }));

    const subtotal = form.line_items.reduce((s, l) => s + Number(l.quantity || 0) * Number(l.unit_price || 0), 0);
    const total = subtotal - Number(form.discount || 0);

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                type: doc.type,
                booking_id: doc.booking_id || null,
                guest_name: form.guest_name, guest_email: form.guest_email || null,
                line_items: form.line_items.map((l) => ({ description: l.description, quantity: Number(l.quantity || 0), unit_price: Number(l.unit_price || 0), amount: 0 })),
                discount: Number(form.discount || 0), currency: form.currency,
                status: form.status, issue_date: form.issue_date, due_date: form.due_date || null, notes: form.notes || null,
            };
            if (doc.id) await api.patch(`/admin/accounting/documents/${doc.id}`, payload);
            else await api.post("/admin/accounting/documents", payload);
            toast.success("Saved");
            onSaved();
        } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="acct-editor">
                <DialogHeader>
                    <DialogTitle className="font-serif text-3xl">{doc.id ? `Edit ${doc.number}` : `New ${doc.type}`}</DialogTitle>
                    <DialogDescription className="capitalize">{doc.type} details and line items.</DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div><label className="eyebrow block mb-2">Guest name</label><input value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })} data-testid="acct-guest-name" className="w-full border border-espresso/20 px-3 py-2 text-sm bg-transparent" /></div>
                    <div><label className="eyebrow block mb-2">Guest email</label><input value={form.guest_email} onChange={(e) => setForm({ ...form, guest_email: e.target.value })} data-testid="acct-guest-email" className="w-full border border-espresso/20 px-3 py-2 text-sm bg-transparent" /></div>
                    <div><label className="eyebrow block mb-2">Issue date</label><input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} className="w-full border border-espresso/20 px-3 py-2 text-sm bg-transparent" /></div>
                    <div><label className="eyebrow block mb-2">Due date</label><input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="w-full border border-espresso/20 px-3 py-2 text-sm bg-transparent" /></div>
                </div>

                <div className="mt-5">
                    <div className="eyebrow mb-2">Line items</div>
                    <div className="space-y-2" data-testid="acct-line-items">
                        {form.line_items.map((l, i) => (
                            <div key={l._k} className="grid grid-cols-12 gap-2 items-center">
                                <input value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} placeholder="Description" data-testid={`acct-line-desc-${i}`} className="col-span-6 border border-espresso/20 px-2 py-1.5 text-sm bg-transparent" />
                                <input type="number" value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} placeholder="Qty" data-testid={`acct-line-qty-${i}`} className="col-span-2 border border-espresso/20 px-2 py-1.5 text-sm bg-transparent text-right" />
                                <input type="number" value={l.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} placeholder="Unit" data-testid={`acct-line-price-${i}`} className="col-span-3 border border-espresso/20 px-2 py-1.5 text-sm bg-transparent text-right" />
                                <button type="button" onClick={() => removeLine(i)} className="col-span-1 text-terracotta flex justify-center"><Trash2 size={14} /></button>
                            </div>
                        ))}
                    </div>
                    <button type="button" onClick={addLine} data-testid="acct-add-line" className="mt-2 inline-flex items-center gap-1.5 text-xs uppercase tracking-widest2 text-forest hover:text-terracotta"><Plus size={13} /> Add line</button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
                    <div><label className="eyebrow block mb-2">Discount ($)</label><input type="number" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} data-testid="acct-discount" className="w-full border border-espresso/20 px-3 py-2 text-sm bg-transparent" /></div>
                    <div><label className="eyebrow block mb-2">Status</label>
                        <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="acct-status" className="w-full border border-espresso/20 px-3 py-2 text-sm bg-transparent">
                            <option value="draft">Draft</option><option value="sent">Sent</option><option value="paid">Paid</option>
                        </select>
                    </div>
                    <div className="md:col-span-2"><label className="eyebrow block mb-2">Notes</label><textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full border border-espresso/20 p-3 text-sm bg-transparent" /></div>
                </div>

                <div className="flex justify-end gap-6 mt-4 text-sm">
                    <div className="text-muted2">Subtotal <span className="text-ink ml-2">${subtotal.toFixed(2)}</span></div>
                    <div className="font-serif text-xl">Total <span data-testid="acct-editor-total" className="ml-2">${total.toFixed(2)}</span></div>
                </div>

                <DialogFooter className="mt-4">
                    <button onClick={onClose} className="px-5 py-3 border border-espresso/30 text-xs uppercase tracking-widest2">Cancel</button>
                    <button onClick={save} disabled={saving} data-testid="acct-save" className="px-5 py-3 bg-forest text-bone text-xs uppercase tracking-widest2 font-semibold disabled:opacity-50">{saving ? "Saving…" : "Save"}</button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function BookingPicker({ onClose, onCreated }) {
    const [bookings, setBookings] = useState([]);
    const [type, setType] = useState("invoice");
    useEffect(() => { api.get("/bookings").then((r) => setBookings(r.data)); }, []);

    const create = async (bookingId) => {
        try { await api.post("/admin/accounting/documents/from-booking", { booking_id: bookingId, type }); toast.success(`${type} created`); onCreated(); }
        catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="acct-booking-picker">
                <DialogHeader>
                    <DialogTitle className="font-serif text-3xl">From booking</DialogTitle>
                    <DialogDescription>Pick a reservation to generate a document from.</DialogDescription>
                </DialogHeader>
                <div className="flex gap-2 mb-4">
                    {["invoice", "quote"].map((tp) => (
                        <button key={tp} onClick={() => setType(tp)} data-testid={`acct-picker-type-${tp}`} className={`px-4 py-2 text-xs uppercase tracking-widest2 border ${type === tp ? "bg-forest text-bone border-forest" : "border-espresso/20"}`}>{tp}</button>
                    ))}
                </div>
                <div className="space-y-2">
                    {bookings.length === 0 && <div className="text-muted2 text-sm">No bookings.</div>}
                    {bookings.map((b) => (
                        <button key={b.id} onClick={() => create(b.id)} data-testid={`acct-pick-${b.id}`} className="w-full text-left border border-espresso/15 px-4 py-3 hover:border-forest hover:bg-forest/5">
                            <div className="font-medium text-sm">{b.guest_name} · {b.cabin_name}</div>
                            <div className="text-xs text-muted2">{b.check_in} → {b.check_out} · ${b.total_price.toFixed(0)}</div>
                        </button>
                    ))}
                </div>
            </DialogContent>
        </Dialog>
    );
}
